import ThemeContext from '../../ThemeContext';
import './TerminologySettings.css';
import React, { useContext, useEffect, useState } from 'react';
import { ENTITIES, validName, setTerminology, pluralName } from '../../../terminology/store';
import { useTerminology, saveTerminology, resetTerminology } from '../../../terminology/TerminologyContext';

export default function TerminologySettings() {
  const { theme } = useContext(ThemeContext);
  const typography = theme.typography || {};
  const colors = theme.colors;
  const { labels, revision, available, loading, refresh, error: loadError } = useTerminology();
  const [draft, setDraft] = useState({});
  const [baseRevision, setBaseRevision] = useState(revision);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [failure, setFailure] = useState('');
  useEffect(() => {
    if (!dirty) { setDraft(labels); setBaseRevision(revision); }
  }, [labels, revision, dirty]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role !== 'superadmin') return <p role="alert">You do not have permission to manage terminology.</p>;
  const submit = async (reset = false) => {
    setFailure(''); setMessage('');
    for (const pair of Object.values(draft)) {
      if (!reset && !validName(pair.singular)) {
        setFailure('Enter a name using 1–80 characters without markup or control characters.'); return;
      }
    }
    setBusy(true);
    try {
      const { data } = reset ? await resetTerminology(baseRevision) : await saveTerminology(Object.fromEntries(Object.entries(draft).map(([key, pair]) => [key, ['clientAdmin', 'clientUser'].includes(key) ? pair : { singular: pair.singular }])), baseRevision);
      setTerminology(data); setDraft(data.labels); setBaseRevision(data.revision); setDirty(false);
      setMessage(reset ? 'Compatibility defaults restored.' : 'Global terminology saved.');
    } catch (err) {
      const errors = err.response?.data?.errors;
      setFailure(errors ? Object.values(errors).flat().join(' ') : err.response?.data?.message || 'Unable to save terminology. Please try again.');
    } finally { setBusy(false); }
  };
  const changeName = (key, value) => {
    setDirty(true);
    setDraft(previous => ({ ...previous, [key]: {
      singular: value, plural: pluralName(value),
    } }));
  };
  return <section className="container py-4 terminology-settings" style={{
    fontFamily: typography.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif',
    fontSize: `${typography.bodyFontSize || 14}px`, color: colors.textPrimary,
    '--term-title-size': `${typography.titleFontSize || 24}px`,
    '--term-bg': colors.bgPrimary, '--term-muted-bg': colors.bgSecondary,
    '--term-text': colors.textPrimary, '--term-muted': colors.textSecondary,
    '--term-border': colors.borderColor, '--term-primary': colors.primary,
    '--term-white': colors.textWhite,
  }}>
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
      <div><h1>Terminology Settings</h1>
        <p className="text-muted mb-0">Set the names everyone sees across OneFinCore. Enter one name for each item. Plurals are generated automatically.</p>
      </div>
      <span className="badge bg-primary p-2">Global configuration · Super Admin</span>
    </div>
    {(failure || loadError) && <p className="alert alert-danger" role="alert">{failure || loadError}</p>}
    {message && <p className="alert alert-success" role="status">{message}</p>}
    {!available && <p className="alert alert-warning" role="alert">Terminology storage is not ready. The terminology database migration must be applied before saving.</p>}
    {dirty && revision !== baseRevision && <p className="alert alert-warning" role="alert">Another administrator changed the settings. Reload before saving.</p>}
    <form onSubmit={event => { event.preventDefault(); submit(); }}>
      {[['Business entities', false], ['Session types', true], ['Optional role labels', 'roles']].map(([heading, sessionTypes]) => <details key={heading} className="mb-4" open={sessionTypes === 'roles' ? undefined : true}>
        <summary className="h4">{heading}</summary>

        <p className="text-muted">{sessionTypes === 'roles' ? 'Client Admin manages a client account; Client User is a team member. These optional labels do not change permissions.' : sessionTypes
          ? 'Name each of the five session types inside a case. The overall Session name is configured separately above.'
          : 'Names used in navigation, management pages, forms, tables, and reports.'}</p>
        <div className="row g-3">
        {Object.entries(ENTITIES).filter(([key, defaults]) => sessionTypes === 'roles' ? ['clientAdmin', 'clientUser'].includes(key) : !['clientAdmin', 'clientUser'].includes(key) && (defaults.group === 'sessionType') === sessionTypes).map(([key, defaults]) => <div key={key} className="col-12 col-lg-6">
          <fieldset className="border rounded p-3 h-100 bg-white" disabled={busy || loading}>
            <legend className="fs-5">{defaults.title}</legend>
            <label className="form-check-label mb-3"><input className="form-check-input me-2" type="checkbox" checked={!!draft[key]} onChange={event => {
              setDirty(true); setDraft(previous => { const next = { ...previous }; if (event.target.checked) next[key] = { singular: defaults.singular, plural: defaults.plural }; else delete next[key]; return next; });
            }} /> Use a custom name</label>
            <div>
              <label className="form-label" htmlFor={`${key}-name`}>Name</label>
              <input id={`${key}-name`} className="form-control" value={draft[key]?.singular ?? defaults.singular} required maxLength={80}
                onChange={event => changeName(key, event.target.value)} />
            </div>
            <p className="small text-muted mt-3 mb-0">{draft[key]
              ? `Preview: Add ${draft[key].singular} · Manage ${draft[key].plural} · ${draft[key].singular} Details`
              : 'Using existing configuration defaults. Edit either field to customize.'}</p>
          </fieldset>
        </div>)}
        </div>
      </details>)}
      <div className="border rounded bg-light p-3 d-flex flex-wrap gap-2 align-items-center" style={{ position: 'sticky', bottom: 0, zIndex: 5 }}>
        <button className="btn btn-primary" type="submit" disabled={busy || loading || !available || !!loadError}>{busy ? 'Saving…' : 'Save Changes'}</button>
        <button className="btn btn-outline-secondary" type="button" disabled={busy || loading || !available || !!loadError} onClick={() => {
          if (window.confirm('Restore existing display defaults for everyone?')) submit(true);
        }}>Restore Defaults</button>
        <button className="btn btn-link" type="button" disabled={busy} onClick={async () => { setDirty(false); setFailure(''); await refresh(); }}>Reload Saved Settings</button>
        <span className="small text-muted ms-auto">{loading ? 'Loading saved names…' : dirty ? 'Unsaved changes' : 'Saved configuration loaded'}</span>
      </div>
    </form>
  </section>;
}
